import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

export const ProductLaunchPerformanceChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, events, currencySettings, isLoading } = useBoardChartData();
    const currencySymbol = getCurrencySymbol(currencySettings?.default);

    const chartData = useMemo(() => {
        if (!events || !finance?.dailyBreakdown) return [];

        const productLaunches = events.filter((e: any) =>
            e.category === 'product' && ['published', 'create'].includes((e.type || e.eventType) as string)
        );
        if (productLaunches.length === 0) return [];

        const sortedDates = Object.keys(finance.dailyBreakdown!).sort();
        const results: { name: string; revenue: number }[] = [];

        productLaunches.forEach((event: any) => {
            const eventDate = event.date?.split('T')[0];
            if (!eventDate || !event.entityId) return;

            const afterDays = sortedDates.filter(d => d >= eventDate);
            let revenue = 0;
            afterDays.forEach(d => {
                const items = finance.dailyBreakdown![d]?.breakdown || [];
                items.forEach((item: any) => {
                    if (item.entityId === event.entityId && item.type === 'REVENUE' && item.category === 'product_sale') {
                        revenue += item.value || 0;
                    }
                });
            });

            const name = event.description?.substring(0, 22) || event.entityId.substring(0, 12);
            results.push({ name, revenue });
        });

        return results.sort((a, b) => b.revenue - a.revenue).slice(0, 10);
    }, [events, finance]);

    const isEmpty = !isLoading && chartData.length === 0;
    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ ...CHART_STYLES.margin.withAxis, left: 80 }}>
                        <CartesianGrid strokeDasharray={CHART_STYLES.grid.strokeDasharray} horizontal={false} stroke={CHART_STYLES.grid.stroke} opacity={CHART_STYLES.grid.opacity} />
                        <XAxis type="number" {...CHART_STYLES.axis} tickFormatter={(v) => formatCompactNumber(v, currencySymbol)} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} />
                        <YAxis dataKey="name" type="category" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} width={80} />
                        <Tooltip contentStyle={CHART_STYLES.tooltip.contentStyle} formatter={(v: number) => formatCurrency(v, currencySymbol)} />
                        <Bar dataKey="revenue" name={t('charts.revenueAfterLaunch', 'Revenue After Launch') as string} fill={CHART_COLORS.revenue} radius={[0, 3, 3, 0]} maxBarSize={24} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
